import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTelegram } from './TelegramProvider';
import { useTon } from './TelegramTonProvider';
import { useLilapeBalance } from './hooks/useLilapeBalance';
import { useGameBalance } from './hooks/useGameBalance';
import { useMultiplier } from './hooks/useMultiplier';
import { useSounds } from './hooks/useSounds';
import { MultiplierBadge } from './components/MultiplierBadge';
import { JACKPOT_CONFIG, formatLilape, BET_AMOUNTS, BUY_OPTIONS, calculateSpins, LILAPE_PER_TON, getWinMultiplier } from './config/gameConfig';
import { PRIZE_POOL_ADDRESS, LILAPE_JETTON_ADDRESS } from './config';
import { buildJettonTransferPayload, getUserJettonWallet } from './jettonTransfer';
// NEW: 1 TON = 1B LILAPE
const FREE_SPINS_PER_DAY = 20;
const WELCOME_BONUS      = 10_000_000; // 10M welcome bonus

const STORAGE_BAL  = 'lilape_slots_bal';
const STORAGE_DATE = 'lilape_slots_date';
const STORAGE_FREE = 'lilape_slots_free';
const STORAGE_STREAK = 'lilape_slots_streak';
const STORAGE_LAST_PLAY = 'lilape_slots_lastplay';
const STORAGE_JACKPOT_PROGRESS = 'lilape_slots_jackpot_progress';
const NOTIFY_URL   = '/.netlify/functions/notify-group';

const STREAK_BONUSES = [0, 50, 100, 200, 500, 1000, 2000, 5000]; // Day 0-7 bonuses
const JACKPOT_TARGET = 100000;

async function notifyGroup(type, data) {
  try {
    await fetch(NOTIFY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, data }),
    });
  } catch { /* silent — never block the game */ }
}

/* ── SLOT THEMES ── */
const STORAGE_THEME = 'lilape_slots_theme';

const THEMES = {
  'ape-jungle': {
    name: '🦍 APE JUNGLE',
    bg: 'linear-gradient(180deg, #1a3a1a 0%, #0d1f0d 100%)',
    accent: '#4CAF50',
    symbols: [
      { id: 'gorilla', img: './slots/ape-jungle/gorilla.png', color: '#FFD700', glow: 'rgba(255,215,0,0.8)' },
      { id: 'banana',  img: './slots/ape-jungle/banana.png',  color: '#FFD60A', glow: 'rgba(255,214,10,0.8)' },
      { id: 'palm',    img: './slots/ape-jungle/palm.png',    color: '#4CAF50', glow: 'rgba(76,175,80,0.8)' },
      { id: 'coconut', img: './slots/ape-jungle/coconut.png', color: '#8D6E63', glow: 'rgba(141,110,99,0.8)' },
      { id: 'parrot',  img: './slots/ape-jungle/parrot.png',  color: '#E91E63', glow: 'rgba(233,30,99,0.8)' },
      { id: 'monkey',  img: './slots/ape-jungle/monkey.png',  color: '#795548', glow: 'rgba(121,85,72,0.8)' },
    ],
    paytable: [
      // Triple matches (~3% total) - rare big wins
      { count: 3, idx: 0, multiplier: 10, label: '🦍 JACKPOT!' },
      { count: 3, idx: 1, multiplier: 5, label: '🍌 BIG WIN!' },
      { count: 3, idx: 2, multiplier: 3,  label: '🌴 WINNER!'  },
      { count: 3, idx: 3, multiplier: 2,  label: '🥥 NICE!'    },
      { count: 3, idx: 4, multiplier: 1.5,  label: '🦜 WIN!'     },
      { count: 3, idx: 5, multiplier: 1,  label: '🐒 WIN!'     },
      // Double matches (~15% total) - small consolation wins
      { count: 2, idx: 0, multiplier: 0.2, label: '🦍🦍'  },
      { count: 2, idx: 1, multiplier: 0.15, label: '🍌🍌'  },
      { count: 2, idx: 2, multiplier: 0.1,  label: '🌴🌴'  },
    ],
  },
  'crypto-vault': {
    name: '💎 CRYPTO VAULT',
    bg: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)',
    accent: '#9C27B0',
    symbols: [
      { id: 'bitcoin',  img: './slots/crypto-vault/bitcoin.png',  color: '#F7931A', glow: 'rgba(247,147,26,0.8)' },
      { id: 'ethereum', img: './slots/crypto-vault/ethereum.png', color: '#627EEA', glow: 'rgba(98,126,234,0.8)' },
      { id: 'diamond',  img: './slots/crypto-vault/diamond.png',  color: '#00E5FF', glow: 'rgba(0,229,255,0.8)' },
      { id: 'vault',    img: './slots/crypto-vault/vault.png',    color: '#C0C0C0', glow: 'rgba(192,192,192,0.8)' },
      { id: 'rocket',   img: './slots/crypto-vault/rocket.png',   color: '#FF6B6B', glow: 'rgba(255,107,107,0.8)' },
      { id: 'chart',    img: './slots/crypto-vault/chart.png',    color: '#00C853', glow: 'rgba(0,200,83,0.8)' },
    ],
    paytable: [
      { count: 3, idx: 0, multiplier: 10, label: '₿ JACKPOT!'  },
      { count: 3, idx: 1, multiplier: 5, label: 'Ξ BIG WIN!'  },
      { count: 3, idx: 2, multiplier: 3,  label: '💎 WINNER!'  },
      { count: 3, idx: 3, multiplier: 2,  label: '🔐 NICE!'    },
      { count: 3, idx: 4, multiplier: 1.5,  label: '🚀 WIN!'     },
      { count: 3, idx: 5, multiplier: 1,  label: '📈 WIN!'     },
      { count: 2, idx: 0, multiplier: 0.2, label: '₿₿'  },
      { count: 2, idx: 1, multiplier: 0.15, label: 'ΞΞ'  },
      { count: 2, idx: 2, multiplier: 0.1,  label: '💎💎'  },
    ],
  },
  'vegas-gold': {
    name: '🎰 VEGAS GOLD',
    bg: 'linear-gradient(180deg, #2d1810 0%, #1a0c08 100%)',
    accent: '#FFD700',
    symbols: [
      { id: 'seven',  img: './slots/vegas-gold/seven.png',  color: '#FF3B30', glow: 'rgba(255,59,48,0.8)' },
      { id: 'cherry', img: './slots/vegas-gold/cherry.png', color: '#E91E63', glow: 'rgba(233,30,99,0.8)' },
      { id: 'bell',   img: './slots/vegas-gold/bell.png',   color: '#FFD700', glow: 'rgba(255,215,0,0.8)' },
      { id: 'lemon',  img: './slots/vegas-gold/lemon.png',  color: '#FFEB3B', glow: 'rgba(255,235,59,0.8)' },
      { id: 'bar',    img: './slots/vegas-gold/bar.png',    color: '#D4AF37', glow: 'rgba(212,175,55,0.8)' },
      { id: 'dollar', img: './slots/vegas-gold/dollar.png', color: '#4CAF50', glow: 'rgba(76,175,80,0.8)' },
    ],
    paytable: [
      { count: 3, idx: 0, multiplier: 10, label: '7️⃣ JACKPOT!'  },
      { count: 3, idx: 1, multiplier: 5, label: '🍒 BIG WIN!'  },
      { count: 3, idx: 2, multiplier: 3,  label: '🔔 WINNER!'   },
      { count: 3, idx: 3, multiplier: 2,  label: '🍋 NICE!'     },
      { count: 3, idx: 4, multiplier: 1.5,  label: '🎰 WIN!'      },
      { count: 3, idx: 5, multiplier: 1,  label: '💵 WIN!'      },
      { count: 2, idx: 0, multiplier: 0.2, label: '7️⃣7️⃣'  },
      { count: 2, idx: 1, multiplier: 0.15, label: '🍒🍒'  },
      { count: 2, idx: 2, multiplier: 0.1,  label: '🔔🔔'  },
    ],
  },
};

// More balanced weights to reduce win frequency
// Each symbol ~16-17% chance, makes triple match ~0.5% each = ~3% total
const WEIGHTS = [15, 16, 17, 17, 17, 18];

// Backend returns emoji, convert to index for themed display
const EMOJI_TO_IDX = { '🦍': 0, '💎': 1, '7️⃣': 2, '🚀': 3, '🍌': 4, '⭐': 5 };
function emojiToIdx(emoji) { return EMOJI_TO_IDX[emoji] ?? 0; }

/* ── HELPERS ── */
function weightedRandomIdx() {
  let r = Math.random() * WEIGHTS.reduce((a, b) => a + b, 0);
  for (let i = 0; i < WEIGHTS.length; i++) { r -= WEIGHTS[i]; if (r <= 0) return i; }
  return WEIGHTS.length - 1;
}
function calcWinTheme(mid, theme, bet = 0) {
  const [a, b, c] = mid;
  const paytable = THEMES[theme].paytable;
  let win = null;
  if (a === b && b === c) win = paytable.find(p => p.count === 3 && p.idx === a) || null;
  else {
    const two = a === b ? a : a === c ? a : b === c ? b : null;
    win = two !== null ? (paytable.find(p => p.count === 2 && p.idx === two) || null) : null;
  }
  // Calculate prize from bet * multiplier
  if (win && bet > 0) {
    return { ...win, prize: Math.floor(bet * win.multiplier) };
  }
  return win;
}
function rnd3Idx() { return [weightedRandomIdx(), weightedRandomIdx(), weightedRandomIdx()]; }
function storage(k, v) { if (v === undefined) return localStorage.getItem(k); localStorage.setItem(k, String(v)); }
function todayStr() { return new Date().toISOString().slice(0, 10); }
function getSpinsLeft() {
  if (storage(STORAGE_DATE) !== todayStr()) { storage(STORAGE_DATE, todayStr()); storage(STORAGE_FREE, FREE_SPINS_PER_DAY); return FREE_SPINS_PER_DAY; }
  return parseInt(storage(STORAGE_FREE) || FREE_SPINS_PER_DAY, 10);
}
function getBalance() {
  const r = storage(STORAGE_BAL); if (r === null) { storage(STORAGE_BAL, WELCOME_BONUS); return WELCOME_BONUS; } return parseInt(r, 10);
}

function getStreak() {
  const lastPlay = storage(STORAGE_LAST_PLAY);
  const streak = parseInt(storage(STORAGE_STREAK) || '0', 10);
  const today = todayStr();
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

  if (lastPlay === today) return streak; // Already played today
  if (lastPlay === yesterday) return Math.min(streak + 1, 7); // Continue streak
  return 1; // Reset streak
}

function getJackpotProgress() {
  return parseInt(storage(STORAGE_JACKPOT_PROGRESS) || '0', 10);
}

/* ── COMPONENT ── */
/* ── SOUND EFFECTS ── */
let audioCtx = null;
const getAudioContext = () => {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
};

const playSound = (type, theme = 'ape-jungle') => {
  try {
    if (!(window.AudioContext || window.webkitAudioContext)) return;
    const ctx = getAudioContext();
    const masterGain = ctx.createGain();
    masterGain.connect(ctx.destination);
    masterGain.gain.value = 0.4;

    if (type === 'spin') {
      // Soft reel spin click - pleasant mechanical sound
      [800, 900, 850, 950, 800].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        const g = ctx.createGain();
        osc.connect(g);
        g.connect(masterGain);
        osc.frequency.value = freq;
        g.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.08);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.08 + 0.06);
        osc.start(ctx.currentTime + i * 0.08);
        osc.stop(ctx.currentTime + i * 0.08 + 0.06);
      });
    } else if (type === 'win') {
      // Coin drop / cha-ching sound - happy ascending notes
      const coinNotes = [1047, 1319, 1568, 1760, 2093];
      coinNotes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        const g = ctx.createGain();
        osc.connect(g);
        g.connect(masterGain);
        osc.frequency.value = freq;
        g.gain.setValueAtTime(0.3, ctx.currentTime + i * 0.08);
        g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.08 + 0.15);
        osc.start(ctx.currentTime + i * 0.08);
        osc.stop(ctx.currentTime + i * 0.08 + 0.15);
      });
      // Add shimmer effect
      for (let i = 0; i < 8; i++) {
        const shimmer = ctx.createOscillator();
        shimmer.type = 'sine';
        const sg = ctx.createGain();
        shimmer.connect(sg);
        sg.connect(masterGain);
        shimmer.frequency.value = 3000 + Math.random() * 2000;
        sg.gain.setValueAtTime(0.05, ctx.currentTime + 0.1 + i * 0.03);
        sg.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1 + i * 0.03 + 0.1);
        shimmer.start(ctx.currentTime + 0.1 + i * 0.03);
        shimmer.stop(ctx.currentTime + 0.1 + i * 0.03 + 0.1);
      }
    } else if (type === 'bigWin') {
      // Celebratory fanfare - Vegas jackpot style
      const fanfare = [523, 659, 784, 1047, 1319, 1568, 2093, 2637];
      fanfare.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        const g = ctx.createGain();
        osc.connect(g);
        g.connect(masterGain);
        osc.frequency.value = freq;
        g.gain.setValueAtTime(0.35, ctx.currentTime + i * 0.1);
        g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.1 + 0.25);
        osc.start(ctx.currentTime + i * 0.1);
        osc.stop(ctx.currentTime + i * 0.1 + 0.25);
      });
      // Coin shower effect
      for (let i = 0; i < 15; i++) {
        const coin = ctx.createOscillator();
        coin.type = 'sine';
        const cg = ctx.createGain();
        coin.connect(cg);
        cg.connect(masterGain);
        coin.frequency.value = 2500 + Math.random() * 3000;
        const startTime = ctx.currentTime + 0.3 + i * 0.05;
        cg.gain.setValueAtTime(0.08, startTime);
        cg.gain.exponentialRampToValueAtTime(0.001, startTime + 0.08);
        coin.start(startTime);
        coin.stop(startTime + 0.08);
      }
    } else if (type === 'nearMiss') {
      // Gentle "almost" sound - not harsh
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      const g = ctx.createGain();
      osc.connect(g);
      g.connect(masterGain);
      osc.frequency.setValueAtTime(600, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.3);
      g.gain.setValueAtTime(0.15, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    }
  } catch (e) { /* silent */ }
};

const SlotMachineGame = ({ onNavigate }) => {
  const { hapticFeedback, showAlert } = useTelegram();
  const { connected, sendTransaction, address, connect } = useTon();
  const { balance: walletBalance } = useLilapeBalance(address);
  const { gameBalance, depositing, withdrawing, deposit, withdraw, useSpin, addWinnings, canSpin, error: gameError } = useGameBalance(address, sendTransaction);
  const { balanceTier, totalMultiplier, applyMultiplier } = useMultiplier();
  const { play: playSound, spinSound, reelStopSound } = useSounds(true);

  // Show deposit/withdraw panel
  const [showWallet, setShowWallet] = useState(false);
  const [depositAmount, setDepositAmount] = useState(500_000_000); // 500M LILAPE default

  // Sound state
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return localStorage.getItem('lilape_sound') !== 'off'; } catch { return true; }
  });

  // Check if user can play jackpot
  const canPlayJackpot = (walletBalance || 0) >= JACKPOT_CONFIG.minBalanceRequired;

  // Theme state
  const [slotTheme, setSlotTheme] = useState(() => storage(STORAGE_THEME) || 'ape-jungle');
  const currentTheme = THEMES[slotTheme];

  // Each reel: [top, mid, bottom] — indices into theme symbols
  const [reels,     setReels]     = useState(() => [rnd3Idx(), rnd3Idx(), rnd3Idx()]);
  const [spinning,  setSpinning]  = useState(false);
  const [stopped,   setStopped]   = useState([true, true, true]);
  const [winInfo,   setWinInfo]   = useState(null);
  const [nearMiss,  setNearMiss]  = useState(false);
  const [balance,   setBalance]   = useState(getBalance);
  const [freeSpins, setFreeSpins] = useState(getSpinsLeft);
  const [buying,    setBuying]    = useState(false);
  const [showPay,   setShowPay]   = useState(false);
  const [bigWin,      setBigWin]      = useState(false);
  const [lightOn,     setLightOn]     = useState(true);
  const [realBet,     setRealBet]     = useState(BET_AMOUNTS[0].lilape); // 50M default
  const [realStatus,  setRealStatus]  = useState('');
  const [streak,      setStreak]      = useState(getStreak);
  const [jackpotProgress, setJackpotProgress] = useState(getJackpotProgress);
  const [showConfetti, setShowConfetti] = useState(false);
  const [streakClaimed, setStreakClaimed] = useState(() => storage(STORAGE_LAST_PLAY) === todayStr());

  const intervalRef = useRef(null);
  const timeouts    = useRef([]);

  // Theme switch handler
  const switchTheme = (newTheme) => {
    if (!spinning) {
      setSlotTheme(newTheme);
      storage(STORAGE_THEME, newTheme);
      playSound('spin', newTheme);
    }
  };

  // Lights blink
  useEffect(() => {
    const t = setInterval(() => setLightOn(p => !p), 600);
    return () => clearInterval(t);
  }, []);

  useEffect(() => () => { clearInterval(intervalRef.current); timeouts.current.forEach(clearTimeout); }, []);

  const handleSpin = useCallback(() => {
    if (spinning || freeSpins <= 0) return;
    try { hapticFeedback?.('light'); } catch {}
    if (soundEnabled) spinSound();
    setWinInfo(null); setBigWin(false); setNearMiss(false);
    setSpinning(true); setStopped([false, false, false]);

    // Update daily streak
    const today = todayStr();
    const lastPlay = storage(STORAGE_LAST_PLAY);
    if (lastPlay !== today) {
      const newStreak = getStreak();
      setStreak(newStreak);
      storage(STORAGE_STREAK, newStreak);
      storage(STORAGE_LAST_PLAY, today);
      const bonus = STREAK_BONUSES[Math.min(newStreak, 7)];
      if (bonus > 0) {
        const newBal = balance + bonus;
        setBalance(newBal);
        storage(STORAGE_BAL, newBal);
        setStreakClaimed(true);
      }
    }

    const newFree = freeSpins - 1;
    setFreeSpins(newFree); storage(STORAGE_FREE, newFree);

    const finalMid = [weightedRandomIdx(), weightedRandomIdx(), weightedRandomIdx()];

    // cycle all rows fast
    intervalRef.current = setInterval(() => {
      setReels([rnd3Idx(), rnd3Idx(), rnd3Idx()]);
    }, 80);

    const stopReel = (idx, delay) => {
      const t = setTimeout(() => {
        setStopped(prev => { const n = [...prev]; n[idx] = true; return n; });
        setReels(prev => {
          const n = [...prev];
          n[idx] = [weightedRandomIdx(), finalMid[idx], weightedRandomIdx()];
          return n;
        });
        try { hapticFeedback?.('light'); } catch {}
        if (soundEnabled) reelStopSound(idx);
      }, delay);
      timeouts.current.push(t);
    };

    stopReel(0, 900);
    stopReel(1, 1400);

    const t3 = setTimeout(() => {
      clearInterval(intervalRef.current);
      const finalReels = [
        [weightedRandomIdx(), finalMid[0], weightedRandomIdx()],
        [weightedRandomIdx(), finalMid[1], weightedRandomIdx()],
        [weightedRandomIdx(), finalMid[2], weightedRandomIdx()],
      ];
      setReels(finalReels);
      setStopped([true, true, true]);
      setSpinning(false);

      // Free spins use base bet of 1M LILAPE for prize calculation
      const freeBet = 1_000_000;
      const win = calcWinTheme(finalMid, slotTheme, freeBet);
      const [a, b, c] = finalMid;
      const hasTwoMatching = (a === b && a !== c) || (a === c && a !== b) || (b === c && b !== a);

      if (win) {
        // Apply balance multiplier to prize
        const multipliedPrize = applyMultiplier(win.prize);
        const winWithMultiplier = { ...win, prize: multipliedPrize, originalPrize: win.prize };
        setWinInfo(winWithMultiplier);
        const nb = balance + multipliedPrize;
        setBalance(nb); storage(STORAGE_BAL, nb);
        const newJP = Math.min(jackpotProgress + win.prize, JACKPOT_TARGET);
        setJackpotProgress(newJP);
        storage(STORAGE_JACKPOT_PROGRESS, newJP);

        // Big win thresholds adjusted for proportional prizes
        if (win.multiplier >= 10) {
          setBigWin(true);
          setShowConfetti(true);
          if (soundEnabled) playSound(win.multiplier >= 50 ? 'jackpot' : 'bigWin');
          notifyGroup('big_win', {
            label:  win.label,
            prize:  win.prize,
            theme:  slotTheme,
          });
          setTimeout(() => setShowConfetti(false), 3000);
        } else if (win.count === 2) {
          if (soundEnabled) playSound('win');
          setNearMiss(true);
        } else {
          if (soundEnabled) playSound('win');
        }
        hapticFeedback?.(win.multiplier >= 10 ? 'heavy' : 'medium');
      } else if (hasTwoMatching) {
        setNearMiss(true);
        if (soundEnabled) playSound('lose');
        try { hapticFeedback?.('medium'); } catch {}
      }
    }, 1900);
    timeouts.current.push(t3);
  }, [spinning, freeSpins, balance, hapticFeedback, slotTheme, jackpotProgress, soundEnabled, spinSound, reelStopSound, playSound, applyMultiplier]);

  const handleBuyLilape = useCallback(async (tonAmount = 1) => {
    if (!connected) { showAlert?.('Connect your TON wallet first'); return; }
    setBuying(true); try { hapticFeedback?.('light'); } catch {}
    try {
      await sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 300,
        messages: [{ address: PRIZE_POOL_ADDRESS, amount: String(Math.floor(tonAmount * 1e9)) }],
      });
      const lilapeAmount = formatLilape(tonAmount * LILAPE_PER_TON);
      try { hapticFeedback?.('heavy'); } catch {}
      showAlert?.(`✅ Отправлено ${tonAmount} TON! Получите ${lilapeAmount} LILAPE через ~5 минут.`);
      notifyGroup('presale_purchase', { ton: tonAmount, lilape: lilapeAmount });
    } catch (err) { showAlert?.(`Error: ${err.message}`); }
    finally { setBuying(false); }
  }, [connected, sendTransaction, hapticFeedback, showAlert]);

  /* ── Handle Deposit ── */
  const handleDeposit = useCallback(async (amount) => {
    if (!connected) {
      if (connect) connect();
      return;
    }
    const success = await deposit(amount);
    if (success) {
      showAlert?.(`✅ Deposited ${formatLilape(amount)} LILAPE to game!`);
      setShowWallet(false);
    }
  }, [connected, connect, deposit, showAlert]);

  /* ── Handle Withdraw ── */
  const handleWithdraw = useCallback(async () => {
    console.log('Withdraw clicked, gameBalance:', gameBalance);
    if (gameBalance <= 0) {
      showAlert?.('No balance to withdraw');
      return;
    }
    try {
      const success = await withdraw(gameBalance);
      console.log('Withdraw result:', success);
      if (success) {
        showAlert?.(`✅ Withdrew ${formatLilape(gameBalance)} LILAPE to wallet!`);
        setShowWallet(false);
      } else {
        showAlert?.(`❌ Withdrawal failed. Please try again.`);
      }
    } catch (err) {
      console.error('Withdraw error:', err);
      showAlert?.(`❌ Error: ${err.message}`);
    }
  }, [gameBalance, withdraw, showAlert]);

  /* ── Game Spin (uses game balance, server-verified) ── */
  const handleGameSpin = useCallback(async () => {
    if (spinning) return;

    if (!canSpin(realBet)) {
      try { hapticFeedback?.('error'); } catch {}
      setRealStatus('Deposit LILAPE first!');
      setShowWallet(true);
      return;
    }

    // Deduct bet from game balance (server-side)
    const betSuccess = await useSpin(realBet, 'slots');
    if (!betSuccess) {
      setRealStatus('Insufficient balance');
      return;
    }

    try { hapticFeedback?.('medium'); } catch {}
    if (soundEnabled) spinSound();
    setWinInfo(null); setBigWin(false); setNearMiss(false); setRealStatus('');
    setSpinning(true); setStopped([false, false, false]);

    const finalMid = [weightedRandomIdx(), weightedRandomIdx(), weightedRandomIdx()];

    // Animate reels
    intervalRef.current = setInterval(() => {
      setReels([rnd3Idx(), rnd3Idx(), rnd3Idx()]);
    }, 80);

    const stopReel = (idx, delay) => {
      const t = setTimeout(() => {
        setStopped(prev => { const n = [...prev]; n[idx] = true; return n; });
        setReels(prev => {
          const n = [...prev];
          n[idx] = [weightedRandomIdx(), finalMid[idx], weightedRandomIdx()];
          return n;
        });
        try { hapticFeedback?.('light'); } catch {}
        if (soundEnabled) reelStopSound(idx);
      }, delay);
      timeouts.current.push(t);
    };

    stopReel(0, 900);
    stopReel(1, 1400);

    const t3 = setTimeout(() => {
      clearInterval(intervalRef.current);
      const finalReels = [
        [weightedRandomIdx(), finalMid[0], weightedRandomIdx()],
        [weightedRandomIdx(), finalMid[1], weightedRandomIdx()],
        [weightedRandomIdx(), finalMid[2], weightedRandomIdx()],
      ];
      setReels(finalReels);
      setStopped([true, true, true]);
      setSpinning(false);

      // Calculate win with bet multiplier
      const betMultiplier = getWinMultiplier(realBet);
      const win = calcWinTheme(finalMid, slotTheme, realBet);
      const [a, b, c] = finalMid;
      const hasTwoMatching = (a === b && a !== c) || (a === c && a !== b) || (b === c && b !== a);

      if (win) {
        // Apply bet multiplier (x2, x10) and balance multiplier
        const prizeWithBetBonus = Math.floor(win.prize * betMultiplier);
        const finalPrize = applyMultiplier(prizeWithBetBonus);

        setWinInfo({ ...win, prize: finalPrize, originalPrize: win.prize, betMultiplier });
        // Credit winnings to server (async, fire and forget for UI responsiveness)
        addWinnings(finalPrize, 'slots', realBet);

        if (win.multiplier >= 10) {
          setBigWin(true);
          setShowConfetti(true);
          if (soundEnabled) playSound(win.multiplier >= 50 ? 'jackpot' : 'bigWin');
          notifyGroup('big_win', { label: win.label, prize: finalPrize, theme: slotTheme });
          setTimeout(() => setShowConfetti(false), 3000);
        } else if (win.count === 2) {
          if (soundEnabled) playSound('win');
          setNearMiss(true);
        } else {
          if (soundEnabled) playSound('win');
        }
        hapticFeedback?.(win.multiplier >= 10 ? 'heavy' : 'medium');
      } else if (hasTwoMatching) {
        setNearMiss(true);
        if (soundEnabled) playSound('lose');
        try { hapticFeedback?.('medium'); } catch {}
      }
    }, 1900);
    timeouts.current.push(t3);
  }, [spinning, realBet, canSpin, useSpin, addWinnings, hapticFeedback, slotTheme, soundEnabled, spinSound, reelStopSound, playSound, applyMultiplier]);

  /* ── Legacy Real LILAPE spin (kept for reference) ── */
  const handleRealSpin = useCallback(async () => {
    // Redirect to game spin with deposit
    if (gameBalance < realBet) {
      setShowWallet(true);
      return;
    }
    handleGameSpin();
  }, [gameBalance, realBet, handleGameSpin]);

  /* ── LIGHTS CONFIG ── */
  const lights = ['#FF3B30','#FFD60A','#34C759','#34C759','#FFD60A','#FF3B30','#FF3B30','#FFD60A','#34C759'];

  return (
    <div style={{ padding: '0 0 96px' }}>

      {/* ── DEPOSIT MODAL ── */}
      {showWallet && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.8)', zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }} onClick={() => setShowWallet(false)}>
          <div style={{
            background: '#1a1a2e', borderRadius: 16, padding: 20, maxWidth: 340, width: '100%',
            border: '1px solid rgba(255,255,255,0.1)',
          }} onClick={e => e.stopPropagation()}>

            {/* If user has LILAPE in wallet — show DEPOSIT mode */}
            {(walletBalance || 0) > 0 ? (
              <>
                <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 16, textAlign: 'center' }}>
                  💰 DEPOSIT TO GAME
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Your Wallet Balance:</div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: '#10B981' }}>
                    {formatLilape(walletBalance)} LILAPE
                  </div>
                </div>

                <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Deposit Amount:</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  {[
                    { lilape: 500_000_000, label: '500M' },
                    { lilape: 1_000_000_000, label: '1B' },
                    { lilape: 5_000_000_000, label: '5B' },
                    { lilape: walletBalance || 0, label: 'MAX', isMax: true },
                  ].map(opt => {
                    const canAfford = (walletBalance || 0) >= opt.lilape && opt.lilape > 0;
                    const isSelected = opt.isMax ? depositAmount === walletBalance : depositAmount === opt.lilape;
                    return (
                      <button key={opt.label} onClick={() => canAfford && setDepositAmount(opt.lilape)} style={{
                        flex: 1, padding: '12px 8px', borderRadius: 10,
                        background: isSelected ? 'rgba(16,185,129,0.2)' : opt.isMax ? 'rgba(255,215,0,0.1)' : 'rgba(255,255,255,0.05)',
                        border: `2px solid ${isSelected ? '#10B981' : opt.isMax ? 'rgba(255,215,0,0.3)' : 'transparent'}`,
                        color: isSelected ? '#10B981' : opt.isMax ? '#FFD700' : canAfford ? '#fff' : '#666',
                        fontSize: 14, fontWeight: 700, cursor: canAfford ? 'pointer' : 'not-allowed',
                        opacity: canAfford ? 1 : 0.5,
                      }}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handleDeposit(depositAmount)}
                  disabled={depositing || !connected || (walletBalance || 0) < depositAmount}
                  style={{
                    width: '100%', padding: '14px', borderRadius: 10, border: 'none',
                    background: depositing || !connected || (walletBalance || 0) < depositAmount
                      ? '#333' : 'linear-gradient(135deg, #10B981, #059669)',
                    color: depositing || !connected || (walletBalance || 0) < depositAmount ? '#666' : '#fff',
                    fontSize: 15, fontWeight: 800, cursor: depositing ? 'not-allowed' : 'pointer',
                  }}
                >
                  {depositing ? '⏳ Depositing...' : `Deposit ${formatLilape(depositAmount)} to Game`}
                </button>

                <div style={{ textAlign: 'center', margin: '12px 0 8px', fontSize: 11, color: '#666' }}>
                  — or buy more LILAPE —
                </div>

                <button
                  onClick={async () => {
                    if (!connected || buying) return;
                    setBuying(true);
                    try {
                      await sendTransaction({
                        validUntil: Math.floor(Date.now() / 1000) + 300,
                        messages: [{ address: PRIZE_POOL_ADDRESS, amount: String(1e9) }],
                      });
                      addWinnings(LILAPE_PER_TON);
                      setShowWallet(false);
                      setRealStatus(`🎉 +1B LILAPE!`);
                    } catch (err) {
                      if (!err.message?.includes('rejected') && !err.message?.includes('cancelled')) {
                        setRealStatus(`Error: ${err.message}`);
                      }
                    } finally {
                      setBuying(false);
                    }
                  }}
                  disabled={buying || !connected}
                  style={{
                    width: '100%', padding: '10px', borderRadius: 10, border: 'none',
                    background: buying || !connected ? '#333' : 'linear-gradient(135deg, #0088CC, #005580)',
                    color: buying || !connected ? '#666' : '#fff',
                    fontSize: 13, fontWeight: 700, cursor: buying ? 'not-allowed' : 'pointer',
                  }}
                >
                  {buying ? '⏳...' : '💎 Buy 1B for 1 TON'}
                </button>
              </>
            ) : (
              /* No LILAPE in wallet — show BUY mode */
              <>
                <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 16, textAlign: 'center' }}>
                  💎 BUY LILAPE
                </div>

                <div style={{ marginBottom: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Rate: 1 TON = 1B LILAPE</div>
                  <div style={{ fontSize: 11, color: '#10B981' }}>Instant credit to game balance!</div>
                </div>

                <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Select Amount:</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  {[
                    { ton: 1, label: '1 TON', lilapeLabel: '1B' },
                    { ton: 5, label: '5 TON', lilapeLabel: '5B' },
                    { ton: 10, label: '10 TON', lilapeLabel: '10B' },
                  ].map(opt => (
                    <button key={opt.ton} onClick={() => setDepositAmount(opt.ton)} style={{
                      flex: 1, padding: '12px 8px', borderRadius: 10,
                      background: depositAmount === opt.ton ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.05)',
                      border: `2px solid ${depositAmount === opt.ton ? '#10B981' : 'transparent'}`,
                      color: depositAmount === opt.ton ? '#10B981' : '#fff',
                      fontSize: 14, fontWeight: 700, cursor: 'pointer',
                    }}>
                      <div>{opt.label}</div>
                      <div style={{ fontSize: 11, color: depositAmount === opt.ton ? '#10B981' : '#888', marginTop: 2 }}>
                        = {opt.lilapeLabel}
                      </div>
                    </button>
                  ))}
                </div>

                <button
                  onClick={async () => {
                    if (!connected || buying) return;
                    setBuying(true);
                    try {
                      const tonAmount = depositAmount;
                      const lilapeAmount = tonAmount * LILAPE_PER_TON;
                      await sendTransaction({
                        validUntil: Math.floor(Date.now() / 1000) + 300,
                        messages: [{ address: PRIZE_POOL_ADDRESS, amount: String(tonAmount * 1e9) }],
                      });
                      addWinnings(lilapeAmount);
                      setShowWallet(false);
                      setRealStatus(`🎉 +${formatLilape(lilapeAmount)} LILAPE!`);
                    } catch (err) {
                      if (!err.message?.includes('rejected') && !err.message?.includes('cancelled')) {
                        setRealStatus(`Error: ${err.message}`);
                      }
                    } finally {
                      setBuying(false);
                    }
                  }}
                  disabled={buying || !connected}
                  style={{
                    width: '100%', padding: '14px', borderRadius: 10, border: 'none',
                    background: buying || !connected ? '#333' : 'linear-gradient(135deg, #0088CC, #005580)',
                    color: buying || !connected ? '#666' : '#fff',
                    fontSize: 15, fontWeight: 800, cursor: buying ? 'not-allowed' : 'pointer',
                  }}
                >
                  {buying ? '⏳ Processing...' : `Buy ${depositAmount}B LILAPE for ${depositAmount} TON`}
                </button>
              </>
            )}

            <button onClick={() => setShowWallet(false)} style={{
              width: '100%', marginTop: 10, padding: '10px', borderRadius: 10,
              background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
              color: 'rgba(255,255,255,0.6)', fontSize: 13, cursor: 'pointer',
            }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── MULTIPLIER BADGE + SOUND TOGGLE ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 10px' }}>
        <div style={{ flex: 1 }}>
          <MultiplierBadge onUpgradeClick={() => onNavigate && onNavigate('buy')} />
        </div>
        <button
          onClick={() => {
            const newState = !soundEnabled;
            setSoundEnabled(newState);
            try { localStorage.setItem('lilape_sound', newState ? 'on' : 'off'); } catch {}
            if (newState) playSound('click');
          }}
          style={{
            width: 44, height: 44, borderRadius: 12,
            background: soundEnabled ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.05)',
            border: `1px solid ${soundEnabled ? 'rgba(16,185,129,0.4)' : 'rgba(255,255,255,0.1)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, cursor: 'pointer',
          }}
        >
          {soundEnabled ? '🔊' : '🔇'}
        </button>
      </div>

      {/* ── CONFETTI ── */}
      {showConfetti && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 9999,
          background: 'transparent', overflow: 'hidden'
        }}>
          {[...Array(50)].map((_, i) => (
            <div key={i} style={{
              position: 'absolute',
              left: `${Math.random() * 100}%`,
              top: '-5%',
              width: `${6 + Math.random() * 8}px`,
              height: `${6 + Math.random() * 8}px`,
              background: ['#FFD700', '#FF6B6B', '#4ECDC4', '#A855F7', '#22C55E'][i % 5],
              borderRadius: Math.random() > 0.5 ? '50%' : '2px',
              animation: `confettiFall ${2 + Math.random()}s linear forwards`,
              animationDelay: `${Math.random() * 0.5}s`,
            }} />
          ))}
          <style>{`
            @keyframes confettiFall {
              0% { transform: translateY(0) rotate(0deg); opacity: 1; }
              100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
            }
          `}</style>
        </div>
      )}

      {/* ── DAILY STREAK ── */}
      <div style={{ margin: '0 10px 10px', padding: '10px 14px', borderRadius: 12,
        background: 'linear-gradient(135deg, rgba(255,107,107,0.1), rgba(255,215,0,0.1))',
        border: '1px solid rgba(255,215,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: '#FFD700', fontWeight: 700 }}>🔥 DAILY STREAK</span>
          <span style={{ fontSize: 14, fontWeight: 900, color: '#FF6B6B' }}>Day {streak}/7</span>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {[1,2,3,4,5,6,7].map(day => (
            <div key={day} style={{
              flex: 1, height: 6, borderRadius: 3,
              background: day <= streak ? 'linear-gradient(90deg, #FFD700, #FF6B6B)' : 'rgba(255,255,255,0.1)',
              transition: 'all 0.3s'
            }} />
          ))}
        </div>
        {!streakClaimed && streak > 0 && (
          <div style={{ marginTop: 8, fontSize: 11, color: '#22C55E', textAlign: 'center' }}>
            🎁 +{STREAK_BONUSES[Math.min(streak, 7)]} bonus coins today!
          </div>
        )}
      </div>

      {/* ── JACKPOT PROGRESS ── */}
      <div style={{ margin: '0 10px 10px', padding: '10px 14px', borderRadius: 12,
        background: 'linear-gradient(135deg, rgba(168,85,247,0.1), rgba(236,72,153,0.1))',
        border: '1px solid rgba(168,85,247,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: '#A855F7', fontWeight: 700 }}>💎 JACKPOT PROGRESS</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#EC4899' }}>
            {Math.round(jackpotProgress / JACKPOT_TARGET * 100)}%
          </span>
        </div>
        <div style={{ height: 10, borderRadius: 5, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 5,
            width: `${Math.min(jackpotProgress / JACKPOT_TARGET * 100, 100)}%`,
            background: 'linear-gradient(90deg, #A855F7, #EC4899, #FFD700)',
            transition: 'width 0.5s ease-out',
            boxShadow: '0 0 10px rgba(168,85,247,0.5)'
          }} />
        </div>
        <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
          {jackpotProgress.toLocaleString()} / {JACKPOT_TARGET.toLocaleString()} to MEGA JACKPOT
        </div>
      </div>

      {/* ── THEME SELECTOR (3 themes) ── */}
      <div style={{ margin: '0 10px 12px', padding: '8px', borderRadius: 14,
        background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {Object.entries(THEMES).map(([key, theme]) => (
            <button key={key} onClick={() => switchTheme(key)} style={{
              flex: 1, padding: '12px 6px', borderRadius: 10, border: 'none',
              background: slotTheme === key ? theme.bg : 'rgba(255,255,255,0.03)',
              color: slotTheme === key ? '#fff' : 'rgba(255,255,255,0.4)',
              fontWeight: 700, fontSize: 11, cursor: spinning ? 'default' : 'pointer',
              transition: 'all 0.3s',
              boxShadow: slotTheme === key ? `0 0 15px ${theme.accent}50` : 'none',
              border: slotTheme === key ? `2px solid ${theme.accent}` : '2px solid transparent',
            }}>{theme.name}</button>
          ))}
        </div>
      </div>

      {/* LILAPE balance bar — show when no free spins */}
      {freeSpins === 0 && (
        <div style={{ margin: '0 10px 10px', padding: '10px 14px', borderRadius: 12,
          background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 10, color: '#10B981', letterSpacing: '0.08em', marginBottom: 2 }}>LILAPE BALANCE</div>
            <div style={{ fontSize: 16, fontWeight: 900, color: '#10B981' }}>
              {connected ? `${(walletBalance || 0).toLocaleString()} LILAPE` : '—'}
            </div>
          </div>
          {!connected && (
            <button onClick={connect} style={{ padding: '8px 16px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, #10B981, #059669)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              Connect
            </button>
          )}
        </div>
      )}

      {/* ── MACHINE ── */}
      <div style={{ margin: '0 10px 16px', position: 'relative' }}>

        {/* Machine body — themed */}
        <div style={{
          borderRadius: '28px 28px 16px 16px',
          background: slotTheme === 'ape-jungle'
            ? 'linear-gradient(170deg, #1a472a 0%, #0d2818 45%, #1a472a 100%)'
            : slotTheme === 'vegas-gold'
            ? 'linear-gradient(170deg, #8B0000 0%, #4a0000 45%, #8B0000 100%)'
            : 'linear-gradient(170deg, #1a1a3e 0%, #0d0d1f 45%, #1a1a3e 100%)',
          border: `3px solid ${currentTheme.accent}`,
          boxShadow: bigWin
            ? `0 0 50px ${currentTheme.accent}99, 0 8px 32px rgba(0,0,0,0.6)`
            : `0 0 20px ${currentTheme.accent}44, 0 8px 32px rgba(0,0,0,0.6)`,
          transition: 'all 0.5s ease',
          overflow: 'hidden',
          paddingBottom: 16,
        }}>

          {/* Top arch overlay */}
          <div style={{
            background: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, transparent 100%)',
            height: 12,
          }}/>

          {/* Lights row — themed */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '6px 0 4px' }}>
            {(slotTheme === 'ape-jungle'
              ? ['#4CAF50','#8BC34A','#CDDC39','#CDDC39','#8BC34A','#4CAF50','#4CAF50','#8BC34A','#CDDC39']
              : slotTheme === 'vegas-gold'
              ? ['#FF3B30','#FFD700','#FF3B30','#FFD700','#FF3B30','#FFD700','#FF3B30','#FFD700','#FF3B30']
              : ['#9C27B0','#E91E63','#FFD700','#FFD700','#E91E63','#9C27B0','#9C27B0','#E91E63','#FFD700']
            ).map((c, i) => (
              <div key={i} style={{
                width: 11, height: 11, borderRadius: '50%',
                background: lightOn ? c : '#444',
                boxShadow: lightOn ? `0 0 8px ${c}, 0 0 16px ${c}` : 'none',
                transition: 'all 0.3s ease',
              }}/>
            ))}
          </div>

          {/* Title sign — themed */}
          <div style={{
            margin: '6px 16px 10px',
            padding: '8px 12px',
            borderRadius: 10,
            background: slotTheme === 'ape-jungle'
              ? 'linear-gradient(135deg, #0d1f0d, #1a3a1a)'
              : slotTheme === 'vegas-gold'
              ? 'linear-gradient(135deg, #1a0c08, #2d1810)'
              : 'linear-gradient(135deg, #0f0f1a, #1a1a2e)',
            border: `2px solid ${currentTheme.accent}`,
            textAlign: 'center',
            boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.5)',
          }}>
            <div style={{
              fontSize: 20, fontWeight: 900, letterSpacing: '0.08em',
              background: slotTheme === 'ape-jungle'
                ? 'linear-gradient(135deg, #4CAF50, #8BC34A, #4CAF50)'
                : slotTheme === 'vegas-gold'
                ? 'linear-gradient(135deg, #FFD700, #FFF8DC, #FFD700)'
                : 'linear-gradient(135deg, #9C27B0, #E91E63, #FFD700)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              {currentTheme.name}
            </div>
            {winInfo ? (
              <div style={{
                fontSize: 13, fontWeight: 800, marginTop: 2,
                color: bigWin ? '#FFD700' : '#34C759',
                animation: nearMiss ? 'shake 0.5s ease' : 'fadeInUp 0.3s ease',
              }}>
                {winInfo.label} +{winInfo.prize.toLocaleString()} LILAPE
                {totalMultiplier > 1 && winInfo.originalPrize && (
                  <div style={{ fontSize: 10, color: balanceTier.color, marginTop: 2 }}>
                    {balanceTier.icon} x{totalMultiplier.toFixed(1)} multiplier applied!
                  </div>
                )}
                {nearMiss && winInfo.count === 2 && (
                  <div style={{ fontSize: 10, color: '#FF9500', marginTop: 2 }}>
                    🔥 ONE MORE FOR JACKPOT!
                  </div>
                )}
              </div>
            ) : nearMiss ? (
              <div style={{
                fontSize: 13, fontWeight: 800, marginTop: 2,
                color: '#FF9500',
                animation: 'shake 0.5s ease',
              }}>
                😱 SO CLOSE! TRY AGAIN!
              </div>
            ) : (
              <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                {spinning ? 'SPINNING…' : 'GOOD LUCK!'}
              </div>
            )}
          </div>

          {/* ── REELS FRAME ── */}
          <div style={{ margin: '0 12px 12px' }}>
            {/* Outer frame — themed */}
            <div style={{
              background: slotTheme === 'ape-jungle'
                ? 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 30%, #4CAF50 50%, #1B5E20 70%, #2E7D32 100%)'
                : slotTheme === 'vegas-gold'
                ? 'linear-gradient(135deg, #D4AF37 0%, #8B6914 30%, #FFD700 50%, #8B6914 70%, #D4AF37 100%)'
                : 'linear-gradient(135deg, #7B1FA2 0%, #4A148C 30%, #9C27B0 50%, #4A148C 70%, #7B1FA2 100%)',
              borderRadius: 14, padding: 4,
              boxShadow: `0 4px 16px rgba(0,0,0,0.5), inset 0 1px 2px ${currentTheme.accent}44`,
            }}>
              {/* Inner dark bezel — themed */}
              <div style={{
                background: slotTheme === 'ape-jungle' ? '#0d1f0d' : slotTheme === 'vegas-gold' ? '#1a0c08' : '#0a0a15',
                borderRadius: 11, padding: 6,
                display: 'flex', gap: 6,
              }}>
                {reels.map((reel, ri) => {
                  const midSym = currentTheme.symbols[reel[1]] || currentTheme.symbols[0];
                  const isWin  = winInfo && (reels.filter(r => r[1] === reel[1]).length >= 2) && stopped[ri];
                  return (
                    <div key={ri} style={{
                      flex: 1,
                      borderRadius: 8,
                      overflow: 'hidden',
                      border: isWin ? `2px solid ${midSym.color}` : `2px solid ${currentTheme.accent}33`,
                      boxShadow: isWin ? `0 0 16px ${midSym.glow}` : 'none',
                      transition: 'all 0.3s ease',
                      background: slotTheme === 'ape-jungle'
                        ? 'linear-gradient(180deg, #2d4a2d 0%, #1a2f1a 50%, #2d4a2d 100%)'
                        : slotTheme === 'vegas-gold'
                        ? 'linear-gradient(180deg, #F5E6C8 0%, #FFFDF0 50%, #F5E6C8 100%)'
                        : 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 50%, #1a1a2e 100%)',
                    }}>
                      {/* TOP row (dim) */}
                      <div style={{
                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                        padding: '4px 2px', height: 40,
                        opacity: stopped[ri] ? 0.35 : 0.6,
                        filter: stopped[ri] ? 'blur(0.5px)' : 'blur(2px)',
                        transition: 'opacity 0.2s',
                      }}>
                        <img src={currentTheme.symbols[reel[0]]?.img} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                      </div>

                      {/* WIN LINE (top) */}
                      <div style={{ height: 2, background: 'linear-gradient(90deg, transparent 0%, #EF4444 50%, transparent 100%)' }}/>

                      {/* MID row (result) */}
                      <div style={{
                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                        padding: '6px 2px', height: 70,
                        filter: !stopped[ri] ? 'blur(2px)' : 'none',
                        transform: stopped[ri] ? 'scale(1)' : 'scale(0.9)',
                        transition: 'all 0.25s ease',
                      }}>
                        <img
                          src={midSym.img}
                          alt=""
                          style={{
                            width: stopped[ri] ? 60 : 50,
                            height: stopped[ri] ? 60 : 50,
                            objectFit: 'contain',
                            filter: isWin ? `drop-shadow(0 0 12px ${midSym.glow})` : 'none',
                            transition: 'all 0.25s ease',
                          }}
                        />
                      </div>

                      {/* WIN LINE (bottom) */}
                      <div style={{ height: 2, background: 'linear-gradient(90deg, transparent 0%, #EF4444 50%, transparent 100%)' }}/>

                      {/* BOTTOM row (dim) */}
                      <div style={{
                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                        padding: '4px 2px', height: 40,
                        opacity: stopped[ri] ? 0.35 : 0.6,
                        filter: stopped[ri] ? 'blur(0.5px)' : 'blur(2px)',
                        transition: 'opacity 0.2s',
                      }}>
                        <img src={currentTheme.symbols[reel[2]]?.img} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── BOTTOM PANEL ── */}
          <div style={{ margin: '0 12px', background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: '10px 12px' }}>
            {/* Balance + Spins */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <div
                style={{
                  textAlign: 'center', cursor: 'pointer',
                  padding: '4px 8px', borderRadius: 8,
                  background: 'rgba(16,185,129,0.1)',
                  border: '1px solid rgba(16,185,129,0.3)',
                }}
                onClick={() => setShowWallet(true)}
              >
                <div style={{ fontSize: 10, color: '#10B981', letterSpacing: '0.1em', marginBottom: 2 }}>BALANCE</div>
                <div style={{
                  fontSize: 16, fontWeight: 900,
                  background: 'linear-gradient(135deg, #D4AF37, #FFF8DC)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontFamily: 'monospace',
                }}>
                  {formatLilape(gameBalance)}
                </div>
                <div style={{ fontSize: 9, color: (walletBalance || 0) > 0 ? '#10B981' : '#0088CC', fontWeight: 600 }}>
                  {(walletBalance || 0) > 0 ? '💰 DEPOSIT' : '💎 BUY'}
                </div>
              </div>

              <div style={{
                width: 2,
                background: 'linear-gradient(180deg, transparent, #B8860B, transparent)',
              }}/>

              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: '#888', letterSpacing: '0.1em', marginBottom: 2 }}>SPINS</div>
                <div style={{
                  fontSize: 16, fontWeight: 900,
                  color: gameBalance >= realBet ? '#34C759' : '#FF3B30',
                  fontFamily: 'monospace',
                }}>
                  {calculateSpins(gameBalance, realBet)}
                </div>
                <div style={{ fontSize: 9, color: '#666' }}>@{formatLilape(realBet)}</div>
              </div>

              <div style={{
                width: 2,
                background: 'linear-gradient(180deg, transparent, #B8860B, transparent)',
              }}/>

              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: '#888', letterSpacing: '0.1em', marginBottom: 2 }}>JACKPOT</div>
                <div style={{
                  fontSize: 16, fontWeight: 900,
                  color: canPlayJackpot ? '#FFD700' : '#555',
                  fontFamily: 'monospace',
                  animation: canPlayJackpot ? 'jackpotPulse 2s ease-in-out infinite' : 'none',
                }}>
                  10T
                </div>
                <div style={{ fontSize: 9, color: canPlayJackpot ? '#FFD700' : '#444' }}>
                  {canPlayJackpot ? '🦍🦍🦍' : '🔒 500B'}
                </div>
              </div>
            </div>

            {/* Bet selector with spins count and bonus */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
              {BET_AMOUNTS.map(b => {
                const spinsAvailable = calculateSpins(gameBalance || 0, b.lilape);
                const isSelected = realBet === b.lilape;
                const canAfford = spinsAvailable > 0;
                return (
                  <button key={b.lilape} onClick={() => !spinning && setRealBet(b.lilape)} style={{
                    flex: 1, padding: '8px 4px', borderRadius: 8,
                    border: `1.5px solid ${isSelected ? '#10B981' : canAfford ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.1)'}`,
                    background: isSelected ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.04)',
                    color: isSelected ? '#10B981' : canAfford ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)',
                    fontWeight: 700, fontSize: 11,
                    cursor: spinning ? 'default' : 'pointer',
                  }}>
                    <div>{b.label}</div>
                    <div style={{ fontSize: 9, marginTop: 2, color: isSelected ? '#10B981' : 'rgba(255,255,255,0.4)' }}>
                      {spinsAvailable} spins
                    </div>
                    <div style={{ fontSize: 9, marginTop: 1, color: b.winMultiplier > 1 ? '#FFD700' : 'rgba(255,255,255,0.3)' }}>
                      {b.bonusLabel}
                    </div>
                  </button>
                );
              })}
            </div>

            {realStatus && (
              <div style={{ textAlign: 'center', marginBottom: 8, fontSize: 12,
                color: realStatus.startsWith('🎉') ? '#10B981' : 'rgba(255,255,255,0.5)' }}>
                {realStatus}
              </div>
            )}

            {/* SPIN button — uses game balance */}
            <button
              onClick={handleGameSpin}
              disabled={spinning || gameBalance < realBet}
              style={{
                width: '100%', padding: '14px 8px', border: 'none', borderRadius: 10,
                background: spinning || gameBalance < realBet
                  ? '#333'
                  : `linear-gradient(135deg, ${currentTheme.accent} 0%, ${currentTheme.accent}cc 100%)`,
                color: spinning || gameBalance < realBet ? '#666' : '#fff',
                fontSize: 16, fontWeight: 800, letterSpacing: '0.1em',
                cursor: spinning || gameBalance < realBet ? 'not-allowed' : 'pointer',
                boxShadow: spinning ? 'none' : `0 4px 16px ${currentTheme.accent}66`,
                transition: 'all 0.2s ease',
                marginBottom: 10,
              }}
            >
              {spinning ? '⏳ SPINNING...' : `🎰 SPIN ${formatLilape(realBet)}`}
            </button>

            {/* Deposit/Buy + Withdraw buttons */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <button
                onClick={() => setShowWallet(true)}
                disabled={!connected}
                style={{
                  flex: 1, padding: '12px 8px', border: 'none', borderRadius: 10,
                  background: connected
                    ? (walletBalance || 0) > 0
                      ? 'linear-gradient(135deg, #10B981, #059669)'
                      : 'linear-gradient(135deg, #0088CC, #005580)'
                    : '#333',
                  color: connected ? '#fff' : '#666',
                  fontSize: 13, fontWeight: 700, cursor: connected ? 'pointer' : 'not-allowed',
                }}
              >
                {(walletBalance || 0) > 0 ? '💰 DEPOSIT' : '💎 BUY LILAPE'}
              </button>
              <button
                onClick={handleWithdraw}
                disabled={!connected || withdrawing || gameBalance <= 0}
                style={{
                  flex: 1, padding: '12px 8px', border: 'none', borderRadius: 10,
                  background: connected && gameBalance > 0 ? 'linear-gradient(135deg, #F59E0B, #D97706)' : '#333',
                  color: connected && gameBalance > 0 ? '#fff' : '#666',
                  fontSize: 13, fontWeight: 700, cursor: connected && gameBalance > 0 ? 'pointer' : 'not-allowed',
                }}
              >
                {withdrawing ? '⏳...' : '💸 WITHDRAW'}
              </button>
            </div>

            {!connected && (
              <button onClick={connect} style={{
                width: '100%', padding: '12px', borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.2)', background: 'transparent',
                color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>
                🔗 Connect Wallet to Play
              </button>
            )}

            <div style={{ textAlign: 'center', marginTop: 8, fontSize: 10, color: '#666' }}>
              Wallet: {formatLilape(walletBalance || 0)} · Game: {formatLilape(gameBalance)} · 80% pool · 20% burn 🔥
            </div>
          </div>
        </div>
      </div>

      {/* ── PAYTABLE ── */}
      <div style={{ padding: '0 10px 16px' }}>
        <button
          onClick={() => setShowPay(p => !p)}
          style={{
            width: '100%', padding: '11px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid rgba(184,134,11,0.3)',
            background: 'rgba(184,134,11,0.07)',
            color: '#D4AF37',
            fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}
        >
          {showPay ? '▲ Hide Paytable' : '▼ View Paytable'}
        </button>

        {showPay && (
          <div style={{ marginTop: 8, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>
              Prize Table — LILAPE Points
            </div>
            {PAYTABLE.map((row, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '9px 14px',
                borderBottom: i < PAYTABLE.length - 1 ? '1px solid var(--border)' : 'none',
                background: row.prize >= 10000 ? 'rgba(212,175,55,0.05)' : 'transparent',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {Array(row.count).fill(row.icon).map((ic, j) => <span key={j} style={{ fontSize: 20 }}>{ic}</span>)}
                  {row.count === 2 && <span style={{ fontSize: 16, color: 'var(--text-muted)' }}>?</span>}
                </div>
                <span style={{
                  fontWeight: 700, fontSize: 13,
                  color: row.prize >= 50000 ? '#FFD700'
                       : row.prize >= 10000 ? '#FF9500'
                       : row.prize >= 1000  ? '#34C759'
                       : 'var(--text-secondary)',
                }}>
                  {row.prize.toLocaleString()} LILAPE
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeInUp { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes jackpotPulse { 0%,100% { opacity:1; } 50% { opacity:0.6; } }
        @keyframes shake { 0%,100% { transform:translateX(0); } 20%,60% { transform:translateX(-5px); } 40%,80% { transform:translateX(5px); } }
        @keyframes confetti { 0% { transform:translateY(0) rotate(0deg); opacity:1; } 100% { transform:translateY(-100px) rotate(360deg); opacity:0; } }
      `}</style>
    </div>
  );
};

export { SlotMachineGame };
// Build trigger Sun Jun 21 11:55:30 CDT 2026
